import React, { useRef, useEffect } from 'react';

/**
 * Mobile-friendly scrollable tab bar for dashboards.
 * On mobile: compact horizontal scroll with icon + short label, no wrapping.
 * On desktop: same as before with full labels.
 */
const DashboardTabs = ({ tabs, activeTab, onTabChange }) => {
  const scrollRef = useRef(null);

  // Auto-scroll active tab into view on mobile — HORIZONTAL ONLY.
  // Using scrollIntoView({block:'nearest'}) was scrolling the whole page vertically,
  // causing a "jumping up and down" UX issue. We now scroll only the inner container.
  useEffect(() => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const activeEl = container.querySelector(`[data-tab-active="true"]`);
    if (!activeEl) return;
    const containerRect = container.getBoundingClientRect();
    const elRect = activeEl.getBoundingClientRect();
    const centerOffset =
      (elRect.left - containerRect.left) - (container.clientWidth / 2) + (activeEl.clientWidth / 2);
    container.scrollBy({ left: centerOffset, behavior: 'smooth' });
  }, [activeTab]);

  return (
    <div
      ref={scrollRef}
      className="flex gap-1.5 md:gap-2 mb-6 md:mb-8 border-b border-slate-200 pb-3 md:pb-4 overflow-x-auto scrollbar-hide md:flex-wrap"
      style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      data-testid="dashboard-tabs"
    >
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          data-tab-active={activeTab === tab.id ? 'true' : 'false'}
          className={`flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm tracking-wider transition-colors whitespace-nowrap flex-shrink-0 ${
            activeTab === tab.id
              ? 'bg-[#E07A3A] text-white'
              : 'text-slate-500 hover:text-[#1A1A1A] hover:bg-slate-50'
          }`}
          data-testid={tab.testId || `tab-${tab.id}`}
        >
          {tab.icon}
          <span className="hidden sm:inline">{tab.label}</span>
          <span className="sm:hidden">{tab.shortLabel || tab.label}</span>
          {tab.count !== undefined && (
            <span className={`px-1.5 py-0.5 text-[10px] rounded-full ${activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'}`}>
              {tab.count}
            </span>
          )}
          {tab.badge > 0 && (
            <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-red-500 text-white font-bold">{tab.badge}</span>
          )}
        </button>
      ))}
    </div>
  );
};

export default DashboardTabs;
